import { TabularExportCell, useTabularDataExport } from '@va/dashboard/util-hooks';
import { useLocalizationContext } from '@va/localization';
import { CommonVisualizationComponentProps } from '@va/types/charts';
import { BaseTimeseriesResponse } from '@va/types/time-series';
import { useReportBlockCtx } from '@va/ui/components/report-block';
import { getLocalizedDate, getTimestampFromIso } from '@va/util/helpers';
import { useCallback } from 'react';

const CURRENT_PERIOD_SEGMENT = 2;
const PREVIOUS_PERIOD_SEGMENT = 1;

type UseTimeseriesExportParams = Pick<CommonVisualizationComponentProps, 'useCurrentData' | 'usePreviousData'> & {
  fileName: string;
};

/**
 * Exports the data of a timeseries RB (one row per time interval, one column per dataset).
 * When the comparison is active, the previous period rows are included as well, marked by the date segment column.
 */
export const useTimeseriesExport = ({ fileName, useCurrentData, usePreviousData }: UseTimeseriesExportParams) => {
  const { showPrevious } = useReportBlockCtx();
  const { data: currentData } = useCurrentData();
  const { data: previousData } = usePreviousData();

  const { translate, locale } = useLocalizationContext();

  const formatRecordDate = useCallback(
    (record: Record<string, any>) => {
      const from = record['date'] as string;
      const until = record['dateUntil'] as string | undefined;
      const fromLabel = getLocalizedDate(getTimestampFromIso(from), locale);
      if (!until || until === from) return fromLabel;
      return `${fromLabel} - ${getLocalizedDate(getTimestampFromIso(until), locale)}`;
    },
    [locale],
  );

  const getRows = useCallback(() => {
    if (!currentData) return undefined;

    const datasetKeys = Object.keys(currentData.items);

    const buildPeriodRows = (data: BaseTimeseriesResponse<string>, segment: number) =>
      data.tabularData.map((record): TabularExportCell[] => [
        formatRecordDate(record),
        segment,
        ...datasetKeys.map((key) => record[key]),
      ]);

    const headerRow: TabularExportCell[] = [
      translate('labels.date'),
      translate('labels.dateSegment'),
      ...datasetKeys.map((key) => translate(currentData.items[key].label)),
    ];

    return [
      headerRow,
      ...(showPrevious && previousData ? buildPeriodRows(previousData, PREVIOUS_PERIOD_SEGMENT) : []),
      ...buildPeriodRows(currentData, CURRENT_PERIOD_SEGMENT),
    ];
  }, [currentData, formatRecordDate, previousData, showPrevious, translate]);

  return useTabularDataExport(fileName, getRows);
};
